/**
 * Pick language-appropriate values from a content block
 * @param {Object} block - Content block object
 * @param {string} language - Current language ("en" or "ja")
 * @returns {Object} - Object with label, value, description in the correct language
 */
export const pickBlock = (block, language) => {
  if (!block) return { label: "", value: "", description: "", iconName: null, imageUrl: null, linkUrl: null, metadata: null, identifier: null };

  return {
    label: language === "ja" && block.labelJa ? block.labelJa : block.labelEn || "",
    value: language === "ja" && block.valueJa ? block.valueJa : block.valueEn || "",
    description:
      language === "ja" && block.descriptionJa
        ? block.descriptionJa
        : block.descriptionEn || "",
    iconName: block.iconName,
    imageUrl: block.imageUrl,
    linkUrl: block.linkUrl,
    metadata: block.metadata,
    identifier: block.identifier,
  };
};

/**
 * Pick language-appropriate values from a navigation item
 * @param {Object} item - Navigation item object
 * @param {string} language - Current language ("en" or "ja")
 * @returns {Object} - Object with label, url, icon, etc. in the correct language
 */
export const pickNav = (item, language) => {
  if (!item) return { label: "", url: "#", linkType: "none", icon: null, isHighlight: false };

  return {
    label: language === "ja" && item.labelJa ? item.labelJa : item.labelEn || "",
    url: item.linkUrl || "#",
    linkType: item.linkType || "none",
    icon: item.iconName,
    isHighlight: item.isHighlight || false,
  };
};

/**
 * Get icon component from Lucide based on icon name string
 * @param {Object} icons - Import of lucide-react icons
 * @param {string} iconName - Name of the icon (e.g., "Users", "Database")
 * @returns {Component|null} - Lucide icon component or null
 */
export const getIconByName = (icons, iconName) => {
  if (!iconName || !icons[iconName]) return null;
  return icons[iconName];
};

/**
 * Find a content block by its identifier
 * @param {Array} blocks - Array of content blocks
 * @param {string} identifier - Identifier to search for
 * @returns {Object|null} - Found block or null
 */
export const findBlockByIdentifier = (blocks, identifier) => {
  return blocks.find((block) => block.identifier === identifier) || null;
};

/**
 * Group content blocks by their blockType
 * @param {Array} blocks - Array of content blocks
 * @returns {Object} - Object with blockType as keys and arrays of blocks as values
 */
export const groupBlocksByType = (blocks) => {
  return blocks.reduce((acc, block) => {
    const type = block.blockType || "other";
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(block);
    return acc;
  }, {});
};
